import './LinkedSlider.scss';
import { CSSProperties, ReactElement, useState } from 'react';
import { Success500 } from '../../../utils/colors';

const NBSP = ' ';

interface LinkedSliderProps {
  label: string;
  /** Current value of this metric at the active point. */
  value: number | null;
  /** This metric's own range across the curve (so the thumb sits at its real position). */
  min: number;
  max: number;
  /** Formatted value shown to the right of the label. */
  valueText: string;
  /** Formatted confidence interval, revealed on hover. */
  ciText?: string;
  /** Fires with the requested raw value; the card snaps it to the nearest fitted point. */
  onValueChange: (value: number) => void;
  accent?: string;
}

/**
 * One of the three interlinked sliders on a budget card. The three are linked
 * (all reflect the same fitted point) but each is positioned by its OWN value
 * within its OWN range — so as spend rises, volume and efficiency move to their
 * own positions rather than all tracking together. Only fitted points are
 * selectable: a change snaps to the nearest point (the card owns that).
 */
export const LinkedSlider = ({
  label,
  value,
  min,
  max,
  valueText,
  ciText,
  onValueChange,
  accent = Success500
}: LinkedSliderProps): ReactElement => {
  const [hovered, setHovered] = useState(false);
  const range = max - min;
  const fillPct = value != null && range > 0 ? ((value - min) / range) * 100 : 0;
  const step = range > 0 ? range / 200 : 1;
  const ciDisplay = ciText && hovered ? `CI: ${ciText}` : NBSP;

  return (
    <div
      className='bb-slider'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='bb-slider-head'>
        <span className='bb-slider-label'>{label}</span>
        <span className='bb-slider-value'>{valueText}</span>
      </div>
      <input
        className='bb-slider-input'
        style={{ '--bb-fill-pct': `${fillPct}%`, '--bb-accent': accent } as CSSProperties}
        type='range'
        min={min}
        max={max}
        step={step}
        value={value ?? min}
        // Unfittable at this point (value is null) — no meaningful position to drag to.
        disabled={value == null}
        aria-label={label}
        onChange={e => onValueChange(Number(e.target.value))}
      />
      <span className='bb-slider-ci' aria-hidden={!ciText}>
        {ciDisplay}
      </span>
    </div>
  );
};
